import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { supabase } from 'lib/supabase';
import { useAuth } from 'contexts/AuthContext';

const EXPERIENCE_COLORS = {
  Beginner: { bg: 'rgba(5,150,105,0.12)', text: '#059669' },
  Intermediate: { bg: 'rgba(212,165,32,0.12)', text: '#D4A520' },
  Advanced: { bg: 'rgba(11,31,59,0.12)', text: '#0B1F3B' },
};

export default function FollowersModal({ isOpen, onClose, profileId, mode, profileName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myFollowing, setMyFollowing] = useState(new Set());

  useEffect(() => {
    if (!isOpen || !profileId) return;
    loadMembers();
    loadMyFollowing();
  }, [isOpen, profileId, mode]);

  const loadMyFollowing = async () => {
    if (!user?.id) return;
    const { data } = await supabase?.from('member_follows')?.select('following_id')?.eq('follower_id', user?.id);
    if (data) setMyFollowing(new Set(data.map(f => f.following_id)));
  };

  const loadMembers = async () => {
    setLoading(true);
    try {
      if (mode === 'followers') {
        const { data } = await supabase?.from('member_follows')?.select('follower:user_profiles!member_follows_follower_id_fkey(id, full_name, avatar_url, title, experience_level)')?.eq('following_id', profileId);
        setMembers(data?.map(d => d?.follower)?.filter(Boolean) || []);
      } else {
        const { data } = await supabase?.from('member_follows')?.select('following:user_profiles!member_follows_following_id_fkey(id, full_name, avatar_url, title, experience_level)')?.eq('follower_id', profileId);
        setMembers(data?.map(d => d?.following)?.filter(Boolean) || []);
      }
    } catch (err) {
      console.error('Load members error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async (memberId) => {
    if (!user?.id) return;
    const isFollowing = myFollowing?.has(memberId);
    const newSet = new Set(myFollowing);
    if (isFollowing) {
      newSet?.delete(memberId);
      await supabase?.from('member_follows')?.delete()?.eq('follower_id', user?.id)?.eq('following_id', memberId);
    } else {
      newSet?.add(memberId);
      await supabase?.from('member_follows')?.insert({ follower_id: user?.id, following_id: memberId });
    }
    setMyFollowing(newSet);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        style={{ border: '1px solid var(--color-border)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <h3 className="font-heading font-semibold text-lg" style={{ color: 'var(--color-foreground)' }}>
            {mode === 'followers' ? `${profileName}'s Followers` : `${profileName} is Following`}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
            </div>
          ) : members?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
              <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>No {mode} yet</p>
            </div>
          ) : (
            members?.map((member) => {
              const expColor = EXPERIENCE_COLORS?.[member?.experience_level] || EXPERIENCE_COLORS?.Beginner;
              const isMutual = mode === 'followers' && myFollowing?.has(member?.id);
              const isMe = member?.id === user?.id;
              return (
                <div
                  key={member?.id}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors border-b"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <button
                    onClick={() => { navigate(`/members/${member?.id}`); onClose(); }}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border" style={{ borderColor: 'var(--color-accent)' }}>
                      <Image src={member?.avatar_url} alt={member?.full_name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-caption font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
                          {member?.full_name}
                        </p>
                        {isMutual && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-caption" style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}>
                            Mutual
                          </span>
                        )}
                      </div>
                      <p className="font-caption text-xs truncate" style={{ color: 'var(--color-muted-foreground)' }}>{member?.title}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-caption font-medium flex-shrink-0" style={{ background: expColor?.bg, color: expColor?.text }}>
                      {member?.experience_level}
                    </span>
                  </button>
                  {!isMe && (
                    <button
                      onClick={() => handleFollowToggle(member?.id)}
                      className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-caption font-medium transition-all"
                      style={myFollowing?.has(member?.id)
                        ? { background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)', border: '1px solid rgba(0,48,87,0.2)' }
                        : { background: 'var(--color-accent)', color: 'var(--color-primary)' }
                      }
                    >
                      {myFollowing?.has(member?.id) ? 'Following' : 'Follow Back'}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
